import {
  ref,
  push,
  query,
  get,
  orderByChild,
  limitToLast,
  endBefore,
  onValue,
} from "firebase/database";
import type { User } from "firebase/auth";
import { db } from "./firebase";
import type { UserProfile } from "./roles";

/** RTDB path for dashboard audit log (append-only via push). */
export const ACTIVITY_PATH = "activity";

/** Logical area of the dashboard; extend when adding new services. */
export const ActivityDomains = {
  FeatureFlags: "feature_flags",
  Users: "users",
  VersionControl: "version_control",
  DynamicLinks: "dynamic_links",
  Faqs: "faqs",
  HappeningNow: "happening_now",
  Broadcasts: "broadcasts",
  ImageHosting: "image_hosting",
  PortalNotifications: "portal_notifications",
} as const;

export type ActivityDomain =
  (typeof ActivityDomains)[keyof typeof ActivityDomains];

export interface ActivityActor {
  uid: string;
  email: string;
  displayName?: string;
}

/** Stored under `activity/{pushId}` */
export interface ActivityEntry {
  id: string;
  /** Unix ms */
  ts: number;
  domain: ActivityDomain;
  /** e.g. create | update | delete | toggle */
  action: string;
  /** e.g. feature_flag — extend per domain */
  resourceType: string;
  resourceId: string;
  summary: string;
  actor: ActivityActor;
  /** Optional structured payload (must be JSON-serializable; no undefined) */
  details?: Record<string, unknown>;
  /** Schema version for future migrations */
  v?: number;
}

/** Payload for `appendActivity` (no `id`; `ts` is set server-side). */
export type ActivityAppendInput = Omit<ActivityEntry, "id" | "ts"> &
  Partial<Pick<ActivityEntry, "ts">>;

function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (value !== undefined) {
      out[key] = value;
    }
  }
  return out as Partial<T>;
}

function sanitizeDetails(
  details: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!details) return undefined;
  const json = JSON.stringify(details);
  return JSON.parse(json) as Record<string, unknown>;
}

export function actorFromAuth(
  user: User | null,
  profile: UserProfile | null,
): ActivityActor | null {
  if (!user?.uid || !user.email) return null;
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName ?? profile?.displayName,
  };
}

const DOMAIN_LABEL: Record<ActivityDomain, string> = {
  [ActivityDomains.FeatureFlags]: "Feature flags",
  [ActivityDomains.Users]: "Users & roles",
  [ActivityDomains.VersionControl]: "App version control",
  [ActivityDomains.DynamicLinks]: "Dynamic links",
  [ActivityDomains.Faqs]: "In-app FAQ",
  [ActivityDomains.HappeningNow]: "Happening now",
  [ActivityDomains.Broadcasts]: "Broadcasts",
  [ActivityDomains.ImageHosting]: "Image hosting",
  [ActivityDomains.PortalNotifications]: "Portal notifications",
};

export function getActivityDomainLabel(domain: ActivityDomain): string {
  return DOMAIN_LABEL[domain] ?? domain;
}

function valToActivityEntries(
  val: Record<string, Omit<ActivityEntry, "id">> | null,
): ActivityEntry[] {
  if (!val) {
    return [];
  }
  const entries: ActivityEntry[] = Object.entries(val).map(([id, row]) => ({
    id,
    ts: row.ts,
    domain: row.domain as ActivityDomain,
    action: row.action,
    resourceType: row.resourceType,
    resourceId: row.resourceId,
    summary: row.summary,
    actor: row.actor,
    details: row.details,
    v: row.v,
  }));
  entries.sort((a, b) => b.ts - a.ts || b.id.localeCompare(a.id));
  return entries;
}

/**
 * Fetches a single page of the audit log (newest first) using a one-time read
 * to limit download cost vs. listening to a large `limitToLast` window.
 *
 * @param before - Row immediately after this page in time order: pass the
 *   `ts` and `id` of the **oldest** entry from the current list (or null for
 *   the first page, i.e. most recent `pageSize` events).
 */
export async function fetchActivityPage(
  pageSize: number,
  before: { ts: number; id: string } | null,
): Promise<ActivityEntry[]> {
  const activityRef = ref(db, ACTIVITY_PATH);
  const q = before
    ? query(
        activityRef,
        orderByChild("ts"),
        endBefore(before.ts, before.id),
        limitToLast(pageSize),
      )
    : query(activityRef, orderByChild("ts"), limitToLast(pageSize));
  const snapshot = await get(q);
  return valToActivityEntries(
    snapshot.val() as Record<string, Omit<ActivityEntry, "id">> | null,
  );
}

/** Larger batches during full export to reduce RTDB round-trips. */
export const ACTIVITY_EXPORT_PAGE_SIZE = 250;

export type FetchAllActivityResult = {
  entries: ActivityEntry[];
  /** True when the safety `maxRows` cap was hit (more rows may exist in RTDB). */
  capped: boolean;
};

/**
 * Walks the audit log oldest-ward until empty or `maxRows`.
 * Combined `entries` are newest-first. Caller should gate on
 * `Permission.EXPORT_ACTIVITY` (RTDB rules cannot see Firestore roles).
 */
export async function fetchAllActivityEntries(options?: {
  maxRows?: number;
  pageSize?: number;
  onProgress?: (loadedCount: number) => void;
}): Promise<FetchAllActivityResult> {
  const maxRows = options?.maxRows ?? 25_000;
  const pageSize = options?.pageSize ?? ACTIVITY_EXPORT_PAGE_SIZE;
  const onProgress = options?.onProgress;
  const all: ActivityEntry[] = [];
  let before: { ts: number; id: string } | null = null;
  let capped = false;

  while (all.length < maxRows) {
    const remaining = maxRows - all.length;
    const batchLimit = Math.min(pageSize, remaining);
    const page: ActivityEntry[] = await fetchActivityPage(batchLimit, before);
    if (page.length === 0) {
      break;
    }
    all.push(...page);
    onProgress?.(all.length);
    const oldest: ActivityEntry = page[page.length - 1];
    before = { ts: oldest.ts, id: oldest.id };
    if (page.length < batchLimit) {
      break;
    }
    if (all.length >= maxRows) {
      capped = true;
      break;
    }
  }

  return { entries: all, capped };
}

/**
 * Append one activity row. Call after successful mutations.
 * Fails silently in dev if logging throws (caller already succeeded).
 */
export async function appendActivity(
  entry: ActivityAppendInput,
): Promise<void> {
  const activityRef = ref(db, ACTIVITY_PATH);
  const payload = omitUndefined({
    ts: entry.ts ?? Date.now(),
    v: 1,
    domain: entry.domain,
    action: entry.action,
    resourceType: entry.resourceType,
    resourceId: entry.resourceId,
    summary: entry.summary,
    actor: entry.actor,
    details: sanitizeDetails(entry.details),
  } as Record<string, unknown>);
  await push(activityRef, payload);
}

export function subscribeToRecentActivity(
  limit: number,
  onData: (entries: ActivityEntry[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const activityRef = ref(db, ACTIVITY_PATH);
  const q = query(activityRef, orderByChild("ts"), limitToLast(limit));

  return onValue(
    q,
    (snapshot) => {
      const val = snapshot.val() as Record<
        string,
        Omit<ActivityEntry, "id">
      > | null;
      onData(valToActivityEntries(val));
    },
    (err) => {
      onError?.(err);
    },
  );
}
